// utils/permissions.js

// --------------------------------------------------
// CAPABILITIES
// --------------------------------------------------
export const PERMISSIONS = {
  VIEW: "view",
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
  DEVICE_CONTROL: "device_control",
  BILLING: "billing",
  SALTO_SYNC: "salto_sync",
};

// --------------------------------------------------
// RESOURCES / PAGES
// --------------------------------------------------
export const RESOURCES = {
  HRMS_DASHBOARD: "hrms.dashboard",
  HRMS_INTERCOMS: "hrms.intercoms",
  HRMS_DIRECTORIES: "hrms.directories",
  HRMS_TENANTS: "hrms.tenants",
  HRMS_PROXY: "hrms.proxy",

  HUCS_DASHBOARD: "hucs.dashboard",
  HUCS_DEVICES: "hucs.devices",
  HUCS_EXTENSIONS: "hucs.extensions",
  HUCS_DIALPLAN: "hucs.dialplan",
  HUCS_APPLICATIONS: "hucs.applications",
  HUCS_STATUS: "hucs.status",
  HUCS_ADVANCED: "hucs.advanced",
};

// --------------------------------------------------
// DEFAULT ROLE PERMISSIONS
// fallback/general permissions
// --------------------------------------------------
const ROLE_PERMISSIONS = {
  GLOBAL: [
    PERMISSIONS.VIEW,
    PERMISSIONS.CREATE,
    PERMISSIONS.EDIT,
    PERMISSIONS.DELETE,
    PERMISSIONS.DEVICE_CONTROL,
    PERMISSIONS.BILLING,
  ],
  SUPERADMIN: [
    PERMISSIONS.VIEW,
    PERMISSIONS.CREATE,
    PERMISSIONS.EDIT,
    PERMISSIONS.DELETE,
    PERMISSIONS.DEVICE_CONTROL,
    PERMISSIONS.BILLING,
  ],
  ADMIN: [
    PERMISSIONS.VIEW,
    PERMISSIONS.CREATE,
    PERMISSIONS.EDIT,
    PERMISSIONS.DELETE,
    PERMISSIONS.DEVICE_CONTROL,
  ],
  MGNT: [
    PERMISSIONS.VIEW,
    PERMISSIONS.CREATE,
    PERMISSIONS.EDIT,
    PERMISSIONS.DELETE,
  ],
  TECH: [
    PERMISSIONS.VIEW,
    PERMISSIONS.DEVICE_CONTROL,
  ],
  SALES: [
    PERMISSIONS.BILLING,
  ],
  USER: [
    PERMISSIONS.VIEW,
  ],
};

// --------------------------------------------------
// RESOURCE-SPECIFIC OVERRIDES
// this is where page/module-specific permissions live
// --------------------------------------------------
const RESOURCE_PERMISSIONS = {
  GLOBAL: {
    "*": ROLE_PERMISSIONS.GLOBAL,
    [RESOURCES.HRMS_TENANTS]: [
      ...ROLE_PERMISSIONS.GLOBAL,
      PERMISSIONS.SALTO_SYNC,
    ],
  },

  SUPERADMIN: {
    "*": ROLE_PERMISSIONS.SUPERADMIN,
  },

  ADMIN: {
    "*": ROLE_PERMISSIONS.ADMIN,
  },

  MGNT: {
    "*": ROLE_PERMISSIONS.MGNT,

    // MGNT can see Intercoms and sync only.
    // No create/edit/delete on this page.
    [RESOURCES.HRMS_INTERCOMS]: [
      PERMISSIONS.VIEW,
      PERMISSIONS.DEVICE_CONTROL,
    ],
  },

  TECH: {
    "*": ROLE_PERMISSIONS.TECH,

    [RESOURCES.HRMS_INTERCOMS]: [
      PERMISSIONS.VIEW,
      PERMISSIONS.DEVICE_CONTROL,
    ],
  },

  SALES: {
    "*": ROLE_PERMISSIONS.SALES,
  },

  USER: {
    "*": ROLE_PERMISSIONS.USER,
  },
};

// --------------------------------------------------
// MODULE ACCESS
// --------------------------------------------------
export const hasModuleAccess = (userData, clientModules, moduleName) => {
  return true;

  // GLOBAL users have access to everything
  // if (userData?.user_role === "GLOBAL") return true;

  // if (!Array.isArray(clientModules)) return false;

  // return clientModules.some(
  //   (module) =>
  //     module?.name &&
  //     module.name.toUpperCase() === moduleName.toUpperCase()
  // );
};

// --------------------------------------------------
// BASIC ROLE PERMISSION CHECK
// keeps old code working
// --------------------------------------------------
export const hasPermission = (userData, permission) => {
  if (!userData?.user_role) return false;

  return ROLE_PERMISSIONS[userData.user_role]?.includes(permission) || false;
};

// --------------------------------------------------
// NEW RESOURCE PERMISSION CHECK
// use this for page/table/button/action permissions
// --------------------------------------------------
export const hasResourcePermission = (userData, resource, permission) => {
  if (!userData?.user_role || !resource || !permission) return false;

  const role = userData.user_role;
  const roleResources = RESOURCE_PERMISSIONS[role];

  if (!roleResources) return false;

  const resourcePermissions = roleResources[resource];
  const fallbackPermissions = roleResources["*"];

  if (Array.isArray(resourcePermissions)) {
    return resourcePermissions.includes(permission);
  }

  return fallbackPermissions?.includes(permission) || false;
};

// Optional helper for multiple checks
export const hasAnyResourcePermission = (userData, resource, permissions = []) => {
  return permissions.some((permission) =>
    hasResourcePermission(userData, resource, permission)
  );
};

// --------------------------------------------------
// ROUTE FILTERING
// --------------------------------------------------
export const filterRoutesByPermissions = (routes, userData, clientModules) => {
  if (!Array.isArray(routes)) {
    return [];
  }

  return routes.filter((route) => {
    if (
      route.name === "Dashboards" ||
      route.path === "/profile" ||
      route.path === "/companyprofile" ||
      route.path === "/companyusers" ||
      route.path === "/companyclients" ||
      route.path === "/companysites" ||
      route.path === "/siteprofile"
    ) {
      return true;
    }

    if (route.name === "HRMS") {
      return hasModuleAccess(userData, clientModules, "HRMS");
    }

    if (route.name === "HUCS") {
      return hasModuleAccess(userData, clientModules, "HUCS");
    }

    return true;
  });
};

// --------------------------------------------------
// BACKWARD COMPATIBILITY
// --------------------------------------------------
export const hasMinimumRole = (userData, requiredRole) => {
  if (!requiredRole) return true;

  if (requiredRole === "USER") {
    return hasPermission(userData, PERMISSIONS.VIEW);
  }

  return hasPermission(userData, PERMISSIONS.EDIT);
};

// --------------------------------------------------
// DISPLAY HELPERS
// --------------------------------------------------
export const getUserAccessLevel = (userData) => {
  switch (userData?.user_role) {
    case "GLOBAL":
      return "Global administrator";
    case "SUPERADMIN":
    case "ADMIN":
      return "Full administrative access";
    case "MGNT":
      return "Management access";
    case "TECH":
      return "Technical access";
    case "SALES":
      return "Sales access";
    case "USER":
      return "View-only access";
    default:
      return "No access";
  }
};